import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Play, Pause, RotateCcw, BookOpen, ChevronRight, Hand, Sword, Cable, Check, Camera, Settings } from 'lucide-react';
import { recordWorkoutCompletion } from '@/pages/ProgressPage';
import MovementTracker from './MovementTracker';
import MovementSummary from './MovementSummary';
import CameraCalibration from './CameraCalibration';
import { hasCalibration, clearCalibration } from '@/utils/gridMapping';
// BodyGrid archived for future refinement
// import BodyGrid from './BodyGrid';
// FlightDeck archived for future refinement
// import FlightDeck from './FlightDeck';

// Tool options - users can select multiple
const TOOLS = [
  { id: 'empty-hand', name: 'Empty Hand', icon: Hand, description: 'Body-only calibration' },
  { id: 'mace', name: 'Mace', icon: Sword, description: 'Weighted density work' },
  { id: 'long-bar', name: 'Long Bar', icon: Cable, description: 'Extended leverage training' },
  { id: 'rope', name: 'Rope', icon: Cable, description: 'Flow and adaptability' }
];

// Hand configuration images - from GntlHandEdit PDF (16 pages) for Empty Hand and Mace
// isHorizontal: true for configurations with hands in horizontal position (for MotorBoat)
const HAND_CONFIGS = [
  { id: 1, name: 'Configuration 1', image: '/hand-configs/hand-config-01.png', isHorizontal: true },
  { id: 2, name: 'Configuration 2', image: '/hand-configs/hand-config-02.png', isHorizontal: false },
  { id: 3, name: 'Configuration 3', image: '/hand-configs/hand-config-03.png', isHorizontal: false },
  { id: 4, name: 'Configuration 4', image: '/hand-configs/hand-config-04.png', isHorizontal: false },
  { id: 5, name: 'Configuration 5', image: '/hand-configs/hand-config-05.png', isHorizontal: false },
  { id: 6, name: 'Configuration 6', image: '/hand-configs/hand-config-06.png', isHorizontal: false },
  { id: 7, name: 'Configuration 7', image: '/hand-configs/hand-config-07.png', isHorizontal: false },
  { id: 8, name: 'Configuration 8', image: '/hand-configs/hand-config-08.png', isHorizontal: true },
  { id: 9, name: 'Configuration 9', image: '/hand-configs/hand-config-09.png', isHorizontal: false },
  { id: 10, name: 'Configuration 10', image: '/hand-configs/hand-config-10.png', isHorizontal: false },
  { id: 11, name: 'Configuration 11', image: '/hand-configs/hand-config-11.png', isHorizontal: true },
  { id: 12, name: 'Configuration 12', image: '/hand-configs/hand-config-12.png', isHorizontal: true },
  { id: 13, name: 'Configuration 13', image: '/hand-configs/hand-config-13.png', isHorizontal: true },
  { id: 14, name: 'Configuration 14', image: '/hand-configs/hand-config-14.png', isHorizontal: false },
  { id: 15, name: 'Configuration 15', image: '/hand-configs/hand-config-15.png', isHorizontal: false },
  { id: 16, name: 'Configuration 16', image: '/hand-configs/hand-config-16.png', isHorizontal: false }
];

// Long Bar specific hand configuration images (7 pages from LongBar.pdf)
// Long Bar configurations - for regular Long Bar exercises (Row-Fwd, Row-Bck)
// These are extracted from LongBar.pdf
const LONGBAR_CONFIGS = [
  { id: 1, name: 'Long Bar Config 1', image: '/longbar-configs/longbar-config-1.png' },
  { id: 2, name: 'Long Bar Config 2', image: '/longbar-configs/longbar-config-2.png' },
  { id: 3, name: 'Long Bar Config 3', image: '/longbar-configs/longbar-config-3.png' },
  { id: 4, name: 'Long Bar Config 4', image: '/longbar-configs/longbar-config-4.png' },
  { id: 5, name: 'Long Bar Config 5', image: '/longbar-configs/longbar-config-5.png' },
  { id: 6, name: 'Long Bar Config 6', image: '/longbar-configs/longbar-config-6.png' },
  { id: 7, name: 'Long Bar Config 7', image: '/longbar-configs/longbar-config-7.png' }
];

// Motorboat-specific config - from LongBarMotor.pdf
// This is the ONLY image to use for MotorBoat exercise
const MOTORBOAT_CONFIG = { 
  id: 1, 
  name: 'Motorboat Grip', 
  image: '/motorboat-configs/motorboat-config-1.png' 
};

// Steer-specific configs for Long Bar - from SteerLong.pdf
// Used ONLY for Steer-R and Steer-L with Long Bar
const STEER_LONGBAR_CONFIGS = [
  { id: 1, name: 'Steer Config 1', image: '/steer-configs/steer-config-1.png' },
  { id: 2, name: 'Steer Config 2', image: '/steer-configs/steer-config-2.png' },
  { id: 3, name: 'Steer Config 3', image: '/steer-configs/steer-config-3.png' },
  { id: 4, name: 'Steer Config 4', image: '/steer-configs/steer-config-4.png' }
];

// Movement GIFs - oscillate between these and hand configs during breaks
// GIFs are named by their movement type (matched to exact exercise names)
const MOVEMENT_GIFS = {
  'Roll-Bck': 'https://customer-assets.emergentagent.com/job_9ef24d93-e44e-4ca5-96a4-258599e1230b/artifacts/r37g9ft4_Roll-Back.gif',
  'Roll-Fwd': 'https://customer-assets.emergentagent.com/job_9ef24d93-e44e-4ca5-96a4-258599e1230b/artifacts/sj8ihbrt_Roll-Fwd.gif',
  'Row-Bck': 'https://customer-assets.emergentagent.com/job_9ef24d93-e44e-4ca5-96a4-258599e1230b/artifacts/d0x00o0r_Row-Bck.gif',
  'Row-Fwd': 'https://customer-assets.emergentagent.com/job_9ef24d93-e44e-4ca5-96a4-258599e1230b/artifacts/kbvew0tt_Row-Fwd.gif',
  'Swim': 'https://customer-assets.emergentagent.com/job_9ef24d93-e44e-4ca5-96a4-258599e1230b/artifacts/6wi0kdj9_Swim.gif',
  'Steer-L': 'https://customer-assets.emergentagent.com/job_9ef24d93-e44e-4ca5-96a4-258599e1230b/artifacts/7f3mzdtp_Steer-L.gif',
  'Steer-R': 'https://customer-assets.emergentagent.com/job_9ef24d93-e44e-4ca5-96a4-258599e1230b/artifacts/uh3j2rut_Steer-R.gif',
  'MotorBoat': 'https://customer-assets.emergentagent.com/job_9ef24d93-e44e-4ca5-96a4-258599e1230b/artifacts/9zm9xa2r_Motorboat.gif',
  'Figure 8-LH': 'https://customer-assets.emergentagent.com/job_9ef24d93-e44e-4ca5-96a4-258599e1230b/artifacts/5yem1cbm_Figure8-LH.gif',
  'Figure 8-RH': 'https://customer-assets.emergentagent.com/job_9ef24d93-e44e-4ca5-96a4-258599e1230b/artifacts/2mmn11ci_Figure8-RH.gif'
};

// Get GIF for a movement (returns null if no specific GIF)
const getMovementGif = (exercise) => {
  return MOVEMENT_GIFS[exercise] || null;
};

// Filtered horizontal-only configs for other exercises
const HAND_CONFIGS_HORIZONTAL = HAND_CONFIGS.filter(c => c.isHorizontal);

// Leg positions - text only (displayed below hand configuration)
const LEG_POSITIONS = [
  'Neutral Squat',
  'Right Front Lunge',
  'Left Front Lunge',
  'Right Side Lunge',
  'Left Side Lunge',
  'Single Leg Right',
  'Single Leg Left'
];

// Rowing exercises - single leg not allowed for these
const ROWING_EXERCISES = ['Row-Fwd', 'Row-Bck'];

// Check if exercise is a rowing exercise
const isRowingExercise = (exercise) => {
  return ROWING_EXERCISES.includes(exercise);
};

// Assessment-aware leg position selection
// If user has completed assessment, prefer "opposite" positions
// For single leg: challenge the weaker balance leg
const getAssessmentAwareLegPosition = (assessmentData, currentExercise = '') => {
  // Don't use single leg for rowing exercises
  const allowSingleLeg = !isRowingExercise(currentExercise);
  
  if (!assessmentData) {
    // No assessment - return random position (exclude single leg)
    const basicPositions = LEG_POSITIONS.filter(p => !p.startsWith('Single Leg'));
    return basicPositions[Math.floor(Math.random() * basicPositions.length)];
  }

  // Get the opposite positions based on assessment
  const oppositeLeg = assessmentData.oppositeLeg; // 'Left' or 'Right'
  const oppositeSideLunge = assessmentData.oppositeSideLunge; // 'Left' or 'Right'
  const weakerBalanceLeg = assessmentData.weakerBalanceLeg; // 'Left', 'Right', or 'Equal'
  
  // Preferred positions based on assessment (challenge the pattern)
  const preferredPositions = [
    'Neutral Squat',
    `${oppositeLeg} Front Lunge`,
    `${oppositeSideLunge} Side Lunge`
  ];
  
  // Add single leg position if balance assessment exists and not rowing
  if (allowSingleLeg && weakerBalanceLeg && weakerBalanceLeg !== 'Equal') {
    // Challenge the weaker balance leg
    preferredPositions.push(`Single Leg ${weakerBalanceLeg}`);
  }
  
  // 70% chance to use preferred (opposite) position, 30% chance for any position
  if (Math.random() < 0.7) {
    return preferredPositions[Math.floor(Math.random() * preferredPositions.length)];
  }
  
  // For random selection, exclude single leg if rowing
  const availablePositions = allowSingleLeg 
    ? LEG_POSITIONS 
    : LEG_POSITIONS.filter(p => !p.startsWith('Single Leg'));
  
  return availablePositions[Math.floor(Math.random() * availablePositions.length)];
};


// Exercises by tool type - Updated per user specifications
// Empty Hand and Mace share the same exercises (NO MotorBoat - Long Bar only)
const EXERCISES_EMPTY_HAND_MACE = [
  'Roll-Fwd',
  'Roll-Bck',
  'Steer-R',
  'Steer-L',
  'Swim',
  'Row-Fwd',
  'Row-Bck'
];

// Long Bar exercises - includes MotorBoat (horizontal positions only)
const EXERCISES_LONG_BAR = [
  'Steer-R',
  'Steer-L',
  'Row-Fwd',
  'Row-Bck',
  'MotorBoat'
];

const EXERCISES_ROPE = [
  'Figure 8-LH',
  'Figure 8-RH'
];

// Journal prompts organized by category
const JOURNAL_PROMPTS = {
  'Body Signal → Mind Insight': [
    "What's the clearest message my body is giving me right now?",
    "Where in my body do I feel the most alive, and what does that part of me want?",
    "What tension survived the workout—and what does that tension represent in my life?"
  ],
  'Effort, Discipline & Threshold': [
    "Where did I meet a limit today, and what did I learn at that edge?",
    "What moment tempted me to quit, and what kept me going?",
    "What did today's effort reveal about how I handle pressure?"
  ],
  'Breath, Regulation & Presence': [
    "How did my breath change my performance or my mindset?",
    "What emotion was strongest in my chest during the hardest moment?",
    "When did I regain control, and how did I do it?"
  ],
  'Mind-Body Alignment': [
    "What thought strengthened me today? What thought weakened me?",
    "What part of my movement felt most 'true' to who I am?",
    "Where did I override my body—and was it wise?"
  ],
  'Martial Arts Lens': [
    "What was the biggest 'fight' in today's session: physical, emotional, or mental?",
    "What pattern showed up on the mat that also shows up in my relationships or work?",
    "Where did I yield smartly? Where did I force unnecessarily?"
  ],
  'Emotional Debrief': [
    "What emotion is strongest now that the workout is over?",
    "What did the exertion strip away? What remains?",
    "If my sweat could speak, what would it say I'm releasing?"
  ],
  'Integration → Tomorrow\'s Action': [
    "What is one behavior I want to reinforce tomorrow because of today's session?",
    "What is one behavior I want to retire?",
    "What did today teach me about the person I am becoming?"
  ]
};

// Grid constants
const GRID_COLS = 6;
const GRID_ROWS = 5;
const CHAMBER_COUNT = 5;
const LEVEL_COUNT = 5;
const CELLS_PER_ROW = 5;
const ANGLES = [0, 45, 90, 135, 180, -45, -90, -135];

// Grid movement types
const RIDE_TYPES = ['chamber-lock', 'level-lock', 'chamber-ride', 'level-ride', 'x-factor', 'hour-glass'];

// Rope movement types - only use levels 2 and 3
const ROPE_RIDE_TYPES = ['chamber-lock', 'level-lock', 'chamber-ride', 'level-ride'];

// Four corner positions (only for X Factor)
const CORNER_POSITIONS = [
  { chamber: 0, level: 0 },   // Level 1, Chamber 1 (bottom-left)
  { chamber: 0, level: 4 },   // Level 5, Chamber 1 (top-left)
  { chamber: 4, level: 0 },   // Level 1, Chamber 5 (bottom-right)
  { chamber: 4, level: 4 }    // Level 5, Chamber 5 (top-right)
];

// Rope configuration image
const ROPE_CONFIG = {
  name: 'Rope Configuration',
  image: '/rope-config.png'
};

const WorkoutGrid = ({ onComplete, sessionTitle }) => {
  // Multi-tool selection - array of selected tools
  const [selectedTools, setSelectedTools] = useState(['empty-hand']);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentPhase, setCurrentPhase] = useState('ready'); // ready, prep, exercise, break, journal, complete
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [phaseSeconds, setPhaseSeconds] = useState(0);
  const [prepSeconds, setPrepSeconds] = useState(0);
  const [currentExercise, setCurrentExercise] = useState('');
  const [currentTool, setCurrentTool] = useState(''); // Current tool for the exercise
  const [nextExercise, setNextExercise] = useState('');
  const [nextTool, setNextTool] = useState('');
  const [cycleCount, setCycleCount] = useState(0);
  const [totalCycles, setTotalCycles] = useState(10);
  
  // BodyGrid view mode archived - keeping state structure for future restoration
  // const [viewMode, setViewMode] = useState('standard');
  
  // Movement Tracking State
  const [isTrackingEnabled, setIsTrackingEnabled] = useState(false);
  const [movementHistory, setMovementHistory] = useState([]);
  const [showMovementSummary, setShowMovementSummary] = useState(false);
  const [currentUserPosition, setCurrentUserPosition] = useState(null);
  const exerciseMovementHistoryRef = useRef([]); // Track movement for current exercise only
  
  // Camera tracking prompt state - shown before workout starts
  const [showTrackingPrompt, setShowTrackingPrompt] = useState(false);
  const [trackingDecisionMade, setTrackingDecisionMade] = useState(false);
  
  // Camera calibration state
  const [showCalibration, setShowCalibration] = useState(false);
  const [calibrationData, setCalibrationData] = useState(null);
  const [isCalibrated, setIsCalibrated] = useState(() => hasCalibration());
  
  // Assessment data for personalized leg positions
  const [assessmentData, setAssessmentData] = useState(() => {
    // Initialize from localStorage synchronously
    const savedAssessment = localStorage.getItem('userAssessment');
    return savedAssessment ? JSON.parse(savedAssessment) : null;
  });
  
  // Box Breathing state for last break
  const [boxBreathPhase, setBoxBreathPhase] = useState('prep'); // prep, exhale, hold1, inhale, hold2
  const [boxBreathSecond, setBoxBreathSecond] = useState(0);
  const [boxBreathCycle, setBoxBreathCycle] = useState(0);
  
  // Break display oscillation - alternate between GIF and hand config
  const [showGifInBreak, setShowGifInBreak] = useState(true); // Start with GIF if available
  
  // Check if this is the last break (final cooldown before journal)
  // This is the break after the final exercise cycle
  const isLastBreak = cycleCount >= totalCycles;
  
  // Box breathing phases: prep (6s), then 2 cycles of exhale(3s)->hold(3s)->inhale(3s)->hold(3s)
  const getBoxBreathInstruction = () => {
    if (boxBreathPhase === 'prep') {
      return 'Prepare to cool down with a box breath. Exhale first.';
    }
    switch (boxBreathPhase) {
      case 'exhale': return 'Exhale...';
      case 'hold1': return 'Hold...';
      case 'inhale': return 'Inhale...';
      case 'hold2': return 'Hold...';
      default: return '';
    }
  };
  
  // Get ball scale based on breathing phase (0.5 to 1.5)
  const getBreathBallScale = () => {
    if (boxBreathPhase === 'prep') return 1;
    
    const progress = boxBreathSecond / 3; // 0 to 1 over 3 seconds
    
    switch (boxBreathPhase) {
      case 'exhale': return 1.5 - (progress * 1); // 1.5 → 0.5
      case 'hold1': return 0.5; // Stay small
      case 'inhale': return 0.5 + (progress * 1); // 0.5 → 1.5
      case 'hold2': return 1.5; // Stay large
      default: return 1;
    }
  };
  
  // Track previous phase to detect phase changes
  const prevPhaseRef = useRef(currentPhase);
  
  // Effect to manage box breathing timing during last break
  useEffect(() => {
    // Reset box breathing when entering break phase
    if (prevPhaseRef.current !== 'break' && currentPhase === 'break') {
      // Use refs to batch state updates on next tick
      setTimeout(() => {
        setBoxBreathPhase('prep');
        setBoxBreathSecond(0);
        setBoxBreathCycle(0);
      }, 0);
    }
    prevPhaseRef.current = currentPhase;
    
    if (currentPhase !== 'break' || !isLastBreak || isPaused) {
      return;
    }
    
    const interval = setInterval(() => {
      setBoxBreathSecond(prev => {
        const newSecond = prev + 1;
        
        // Prep phase: 6 seconds
        if (boxBreathPhase === 'prep') {
          if (newSecond >= 6) {
            setBoxBreathPhase('exhale');
            return 0;
          }
          return newSecond;
        }
        
        // Breathing phases: 3 seconds each
        if (newSecond >= 3) {
          // Transition to next phase
          switch (boxBreathPhase) {
            case 'exhale':
              setBoxBreathPhase('hold1');
              break;
            case 'hold1':
              setBoxBreathPhase('inhale');
              break;
            case 'inhale':
              setBoxBreathPhase('hold2');
              break;
            case 'hold2':
              // Complete one breath cycle
              setBoxBreathCycle(c => c + 1);
              setBoxBreathPhase('exhale');
              break;
            default:
              break;
          }
          return 0;
        }
        
        return newSecond;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [currentPhase, isLastBreak, isPaused, boxBreathPhase]);

  // Effect to oscillate between GIF and hand config during prep and regular breaks
  // GIF shows for 8 seconds, hand config for 4 seconds
  useEffect(() => {
    // Only oscillate during prep or regular breaks (not last break with box breathing)
    const shouldOscillate = currentPhase === 'prep' || (currentPhase === 'break' && !isLastBreak);
    if (!shouldOscillate || isPaused) {
      return;
    }
    
    // Use a cycle timer - GIF for 8s, hand config for 4s
    let timeInCycle = 0;
    const cycleLength = 12; // 12 second total cycle
    const gifDuration = 8; // GIF shows for first 8 seconds
    
    const oscillateInterval = setInterval(() => {
      timeInCycle = (timeInCycle + 1) % cycleLength;
      setShowGifInBreak(timeInCycle < gifDuration);
    }, 1000);
    
    return () => clearInterval(oscillateInterval);
  }, [currentPhase, isLastBreak, isPaused]);
  
  // Reset GIF display when entering prep or break phase
  useLayoutEffect(() => {
    if (currentPhase === 'prep' || currentPhase === 'break') {
      setShowGifInBreak(true);
    }
  }, [currentPhase]);

  // Grid position state - displayed in prep/break screens
  const [currentChamber, setCurrentChamber] = useState(1);
  const [currentLevel, setCurrentLevel] = useState(1);
  
  // Grid line state (for Rope - 6x5 grid)
  const [linePosition, setLinePosition] = useState({ col: 3, row: 2 });
  const [lineAngle, setLineAngle] = useState(0);
  
  // 5x5 Grid state for bar animation
  const [rideType, setRideType] = useState('chamber-lock');
  const [barPosition, setBarPosition] = useState({ chamber: 0, level: 0 });
  const [startPosition, setStartPosition] = useState({ chamber: 0, level: 0 });
  const [nextRideType, setNextRideType] = useState('chamber-lock');
  const [nextStartPosition, setNextStartPosition] = useState({ chamber: 0, level: 0 });
  
  // Configuration - one hand image and one leg position text per exercise
  const [currentHandConfig, setCurrentHandConfig] = useState(null);
  const [currentLegPosition, setCurrentLegPosition] = useState('');
  
  // Journal state
  const [journalSeconds, setJournalSeconds] = useState(0);
  const [currentPrompt, setCurrentPrompt] = useState({ category: '', prompt: '' });
  const [journalEntry, setJournalEntry] = useState('');
  
  const intervalRef = useRef(null);
  const breakIntervalRef = useRef(null);
  const journalIntervalRef = useRef(null);
  const selectedToolsRef = useRef(selectedTools);
  const cycleCountRef = useRef(0);

  // Keep refs in sync with state
  useEffect(() => {
    selectedToolsRef.current = selectedTools;
  }, [selectedTools]);
  
  useEffect(() => {
    cycleCountRef.current = cycleCount;
  }, [cycleCount]);

  // Movement Tracking Handlers
  // Camera stays active throughout workout, but only records during exercise phase
  // Uses HAND positions for precise grid tracking (user's hands map to chambers/levels)
  const handlePositionUpdate = useCallback((positionData) => {
    const { body, leftHand, rightHand } = positionData;
    
    // Prefer right hand, fallback to left hand, then body center
    // Hands give more precise control for the 5x5 grid
    const primaryPosition = rightHand || leftHand || body;
    
    if (!primaryPosition) return;
    
    setCurrentUserPosition(primaryPosition);
    
    // Only record movement data during exercise phase
    if (currentPhase === 'exercise') {
      // Add to current exercise movement history
      exerciseMovementHistoryRef.current.push({
        chamber: primaryPosition.chamber,
        level: primaryPosition.level,
        timestamp: positionData.timestamp,
        confidence: positionData.confidence,
        source: rightHand ? 'rightHand' : (leftHand ? 'leftHand' : 'body'),
      });
      
      // Also add to overall session history
      setMovementHistory(prev => [...prev, {
        chamber: primaryPosition.chamber,
        level: primaryPosition.level,
        timestamp: positionData.timestamp,
        exercise: currentExercise,
        rideType: rideType,
        source: rightHand ? 'rightHand' : (leftHand ? 'leftHand' : 'body'),
      }]);
    }
  }, [currentPhase, currentExercise, rideType]);

  const handleTrackingStart = useCallback(() => {
    console.log('Movement tracking started - camera will stay active for entire workout');
    setIsTrackingEnabled(true);
    // Don't reset history here - let it accumulate across exercises
  }, []);

  const handleTrackingStop = useCallback(() => {
    console.log('Movement tracking stopped');
    setIsTrackingEnabled(false);
    setCurrentUserPosition(null);
    // Show summary when user stops tracking (if they have any data)
    if (movementHistory.length > 0) {
      setShowMovementSummary(true);
    }
  }, [movementHistory.length]);

  // Show movement summary when workout completes (journal phase) if tracking captured data
  useEffect(() => {
    // Show summary when entering journal phase with any movement data
    if (currentPhase === 'journal' && movementHistory.length > 0) {
      // Delay slightly to ensure smooth transition
      const timer = setTimeout(() => {
        setShowMovementSummary(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentPhase, movementHistory.length]);

  // Function to manually show the summary
  const showSummary = useCallback(() => {
    if (movementHistory.length > 0) {
      setShowMovementSummary(true);
    }
  }, [movementHistory.length]);

  // Track exercise-specific movement for per-exercise analysis
  // Reset per-exercise history when starting new exercise cycle
  useEffect(() => {
    if (currentPhase === 'exercise') {
      // Start fresh for this exercise's tracking
      exerciseMovementHistoryRef.current = [];
    }
  }, [currentPhase, cycleCount]);

  const TOTAL_SESSION_TIME = 15 * 60; // 15 minutes
  const PREP_DURATION = 30; // 30 seconds
  const EXERCISE_DURATION = 60; // 1 minute
  const BREAK_DURATION = 30; // 30 seconds
  const POSITION_CHANGE_INTERVAL = 10; // 10 seconds for grid movement
  const X_FACTOR_INTERVAL = 15; // 15 seconds per diagonal movement
  const JOURNAL_DURATION = 5 * 60; // 5 minutes

  // Toggle tool selection
  const toggleTool = useCallback((toolId) => {
    setSelectedTools(prev => {
      if (prev.includes(toolId)) {
        // Don't allow deselecting if it's the only tool
        if (prev.length === 1) return prev;
        return prev.filter(t => t !== toolId);
      }
      return [...prev, toolId];
    });
  }, []);

  // Get exercises for a specific tool
  const getExercisesForTool = useCallback((tool) => {
    switch (tool) {
      case 'empty-hand':
      case 'mace':
        return EXERCISES_EMPTY_HAND_MACE;
      case 'long-bar':
        return EXERCISES_LONG_BAR;
      case 'rope':
        return EXERCISES_ROPE;
      default:
        return EXERCISES_EMPTY_HAND_MACE;
    }
  }, []);

  // Get random exercise based on selected tools and current cycle
  // If rope is the ONLY selected tool, use it for all cycles
  // If rope is selected WITH other tools, use rope only in last 2 cycles
  const getRandomExercise = useCallback((cycleNum = 0, totalCyclesCount = 10) => {
    const tools = selectedToolsRef.current;
    const hasRope = tools.includes('rope');
    const onlyRope = hasRope && tools.length === 1;
    const isLastTwoCycles = cycleNum >= totalCyclesCount - 1; // Last 2 cycles
    
    let toolToUse;
    
    if (onlyRope) {
      // If ONLY rope is selected, use rope for ALL exercises
      toolToUse = 'rope';
    } else if (hasRope && isLastTwoCycles) {
      // Force rope for last 2 exercises when mixed with other tools
      toolToUse = 'rope';
    } else {
      // Get non-rope tools if we have rope but not in last 2 cycles
      const availableTools = hasRope && !isLastTwoCycles 
        ? tools.filter(t => t !== 'rope') 
        : tools;
      
      // If no tools available, default to empty-hand
      if (availableTools.length === 0) {
        toolToUse = 'empty-hand';
      } else {
        toolToUse = availableTools[Math.floor(Math.random() * availableTools.length)];
      }
    }
    
    const exercises = getExercisesForTool(toolToUse);
    const exercise = exercises[Math.floor(Math.random() * exercises.length)];
    
    return { exercise, tool: toolToUse };
  }, [getExercisesForTool]);

  // Get random hand configuration based on tool and exercise
  // Exercise-specific hand configs for Long Bar
  const getRandomHandConfig = useCallback((forTool = 'empty-hand', forExercise = '') => {
    const isMotorboats = forExercise === 'MotorBoat';
    const isSteering = forExercise === 'Steer-R' || forExercise === 'Steer-L';
    
    if (forTool === 'long-bar') {
      // For MotorBoat, ALWAYS use the dedicated motorboat config
      if (isMotorboats) {
        return MOTORBOAT_CONFIG;
      }
      // For Steering, use the dedicated steer configs
      if (isSteering) {
        return STEER_LONGBAR_CONFIGS[Math.floor(Math.random() * STEER_LONGBAR_CONFIGS.length)];
      }
      // For other Long Bar exercises (Row Forward, Row Backward), use regular configs
      return LONGBAR_CONFIGS[Math.floor(Math.random() * LONGBAR_CONFIGS.length)];
    }
    // Default to standard hand configs for empty-hand and mace
    const configs = isMotorboats ? HAND_CONFIGS_HORIZONTAL : HAND_CONFIGS;
    return configs[Math.floor(Math.random() * configs.length)];
  }, []);

  // Get random leg position (text) - assessment-aware
  // Pass current exercise to exclude single leg for rowing
  const getRandomLegPosition = useCallback((exercise = '') => {
    return getAssessmentAwareLegPosition(assessmentData, exercise);
  }, [assessmentData]);

  // Get random chamber (1-5)
  const getRandomChamber = useCallback(() => {
    return Math.floor(Math.random() * 5) + 1;
  }, []);

  // Get random level (1-5)
  const getRandomLevel = useCallback(() => {
    return Math.floor(Math.random() * 5) + 1;
  }, []);

  // Get random ride configuration - open positions except X Factor uses corners
  // For Rope: only levels 2-3, no X Factor
  // For MotorBoat: only levels 1-2 (indices 0-1)
  const getRandomRideConfig = useCallback((forTool = 'empty-hand', forExercise = '') => {
    let type;
    let startPosition;
    const isMotorboats = forExercise === 'MotorBoat';
    
    if (forTool === 'rope') {
      // Rope uses only chamber-lock, level-lock, chamber-ride, level-ride
      // And only levels 2 and 3 (index 1 and 2)
      type = ROPE_RIDE_TYPES[Math.floor(Math.random() * ROPE_RIDE_TYPES.length)];
      startPosition = {
        chamber: Math.floor(Math.random() * 5), // Any chamber 0-4
        level: Math.random() < 0.5 ? 1 : 2       // Only levels 2 or 3 (index 1 or 2)
      };
    } else if (isMotorboats) {
      // MotorBoat: LOCKED at Level 3 (index 2) + Chamber 3 (index 2).
      // Both axes are fixed — the cursor sits dead-center and does not move.
      // Ride type is picked only so the UI label resolves cleanly.
      type = ROPE_RIDE_TYPES[Math.floor(Math.random() * ROPE_RIDE_TYPES.length)];
      startPosition = {
        chamber: 2,
        level: 2,
      };
    } else {
      // Other tools can use any position and all ride types
      type = RIDE_TYPES[Math.floor(Math.random() * RIDE_TYPES.length)];
      
      if (type === 'x-factor') {
        // X Factor must start from a corner
        startPosition = { ...CORNER_POSITIONS[Math.floor(Math.random() * CORNER_POSITIONS.length)] };
      } else {
        // Other patterns can start anywhere on the grid
        startPosition = {
          chamber: Math.floor(Math.random() * 5), // Any chamber 0-4
          level: Math.floor(Math.random() * 5)    // Any level 0-4
        };
      }
    }
    
    return { rideType: type, startPosition };
  }, []);

  // Calculate smooth bar position based on ride type
  // KEY BEHAVIOR:
  // 1. Cursor STARTS exactly at the position shown on prep/break screen
  // 2. Pattern continues from that point
  // 3. When cursor reaches grid boundary, pattern reverses
  // 4. Exercise can end at any position
  const calculateBarPosition = useCallback((elapsedMs, rideTypeParam, startPos, forTool = 'empty-hand', forExercise = '') => {
    // At the very start, return exact start position
    if (elapsedMs <= 0) {
      return { chamber: startPos.chamber, level: startPos.level };
    }
    
    // Level constraints based on tool/exercise
    const isMotorboats = forExercise === 'MotorBoat';
    let minLevel, maxLevel;
    if (forTool === 'rope') {
      minLevel = 1;
      maxLevel = 2;
    } else if (isMotorboats) {
      // Lock level to Level 3 (index 2). Collapsing min === max means the
      // oscillate helper returns a single static value, so the cursor
      // stays put regardless of which ride type was selected.
      minLevel = 2;
      maxLevel = 2;
    } else {
      minLevel = 0;
      maxLevel = 4;
    }
    
    // Chamber restrictions based on twist direction (for chamber rides)
    // Twist LEFT bias: restrict to chambers 3, 4, 5 (indices 2, 3, 4) - right side of body
    // Twist RIGHT bias: restrict to chambers 1, 2, 3 (indices 0, 1, 2) - left side of body
    // This counter-rotates to restore balance
    let minChamber = 0;
    let maxChamber = 4;

    // Motorboat override: lock chamber to Chamber 3 (index 2) regardless of
    // ride type or twist direction. Combined with the level lock above, the
    // cursor stays glued to the dead-center (L3, C3) dot.
    if (isMotorboats) {
      minChamber = 2;
      maxChamber = 2;
    } else {
      const twistDirection = assessmentData?.twistDirection;
      if (rideType === 'chamber-ride' && twistDirection) {
        if (twistDirection === 'left') {
          // Left twist bias: work chambers 3, 4, 5 (indices 2, 3, 4)
          minChamber = 2;
          maxChamber = 4;
        } else if (twistDirection === 'right') {
          // Right twist bias: work chambers 1, 2, 3 (indices 0, 1, 2)
          minChamber = 0;
          maxChamber = 2;
        }
      }
    }
    
    const elapsedSec = elapsedMs / 1000;
    
    let chamber = startPos.chamber;
    let level = startPos.level;
    
    // Helper: Calculate bouncing position given start, speed, time, and bounds
    // Returns a value that oscillates between min and max, starting from 'start'
    const oscillate = (start, speed, time, min, max) => {
      const range = max - min;
      if (range === 0) return min;
      
      // Total distance traveled (can be positive or negative based on speed sign)
      const totalDistance = speed * time;
      
      // Position relative to min (normalized to 0 to range)
      const startFromMin = start - min;
      
      // Calculate raw position
      let rawPos = startFromMin + totalDistance;
      
      // Use modulo-based bouncing for smooth oscillation
      // One full cycle = 2 * range (go to end and back)
      const cycleLength = 2 * range;
      
      // Normalize to positive cycle
      rawPos = rawPos % cycleLength;
      if (rawPos < 0) rawPos += cycleLength;
      
      // Convert cycle position to actual position
      let result;
      if (rawPos <= range) {
        result = min + rawPos;
      } else {
        result = max - (rawPos - range);
      }
      
      return Math.max(min, Math.min(max, result));
    };
    
    switch (rideTypeParam) {
      case 'chamber-lock': {
        // CHAMBER LOCK: Vertical movement in ONE chamber only
        // Cursor bounces up and down in a single column the entire exercise
        const levelRange = maxLevel - minLevel;
        const speed = levelRange / 15; // 15 seconds to traverse full range
        
        const midLevel = (minLevel + maxLevel) / 2;
        const direction = startPos.level < midLevel ? 1 : -1;
        
        level = oscillate(startPos.level, speed * direction, elapsedSec, minLevel, maxLevel);
        chamber = Math.max(minChamber, Math.min(maxChamber, startPos.chamber)); // LOCKED - never changes
        break;
      }
      
      case 'level-lock': {
        // LEVEL LOCK: Horizontal movement in ONE level only
        // Cursor bounces left and right in a single row the entire exercise
        const chamberRange = maxChamber - minChamber;
        const speed = chamberRange / 15; // 15 seconds to traverse full range
        
        const midChamber = (minChamber + maxChamber) / 2;
        const direction = startPos.chamber < midChamber ? 1 : -1;
        
        chamber = oscillate(startPos.chamber, speed * direction, elapsedSec, minChamber, maxChamber);
        level = Math.max(minLevel, Math.min(maxLevel, startPos.level)); // LOCKED - never changes
        break;
      }
      
      case 'chamber-ride': {
        // CHAMBER RIDE: Snake pattern through chambers (vertical sweeps)
        // - Move vertically through levels in current chamber
        // - At level 1 or 5, step ONE chamber over
        // - Continue in OPPOSITE vertical direction
        const levelRange = maxLevel - minLevel;
        const chamberRange = maxChamber - minChamber;
        
        // Time to sweep one column vertically
        const sweepTime = 12; // 12 seconds per vertical sweep
        const totalSweeps = elapsedSec / sweepTime;
        const currentSweep = Math.floor(totalSweeps);
        const progressInSweep = totalSweeps - currentSweep;
        
        // Chamber advances by one each sweep (bounces at edges)
        const startChamber = startPos.chamber;
        let chamberOffset = currentSweep;
        
        // Calculate chamber with bouncing at edges
        let effectiveChamber = startChamber;
        let chamberDir = startPos.chamber <= 2 ? 1 : -1; // Initial direction
        for (let i = 0; i < chamberOffset; i++) {
          effectiveChamber += chamberDir;
          if (effectiveChamber >= maxChamber) {
            effectiveChamber = maxChamber;
            chamberDir = -1;
          } else if (effectiveChamber <= minChamber) {
            effectiveChamber = minChamber;
            chamberDir = 1;
          }
        }
        chamber = effectiveChamber;
        
        // Level sweeps up and down, alternating direction each sweep
        const sweepGoingUp = (currentSweep % 2 === 0) ? (startPos.level <= 2) : (startPos.level > 2);
        if (sweepGoingUp) {
          level = minLevel + (progressInSweep * levelRange);
        } else {
          level = maxLevel - (progressInSweep * levelRange);
        }
        break;
      }
      
      case 'level-ride': {
        // LEVEL RIDE: Snake pattern through levels (horizontal sweeps)
        // - Move horizontally through chambers on current level
        // - At chamber 1 or 5, step ONE level up or down
        // - Continue in OPPOSITE horizontal direction
        const levelRange = maxLevel - minLevel;
        const chamberRange = maxChamber - minChamber;
        
        // Time to sweep one row horizontally
        const sweepTime = 12; // 12 seconds per horizontal sweep
        const totalSweeps = elapsedSec / sweepTime;
        const currentSweep = Math.floor(totalSweeps);
        const progressInSweep = totalSweeps - currentSweep;
        
        // Level advances by one each sweep (bounces at edges)
        const startLevel = startPos.level;
        let levelOffset = currentSweep;
        
        // Calculate level with bouncing at edges
        let effectiveLevel = startLevel;
        let levelDir = startPos.level <= 2 ? 1 : -1; // Initial direction
        for (let i = 0; i < levelOffset; i++) {
          effectiveLevel += levelDir;
          if (effectiveLevel >= maxLevel) {
            effectiveLevel = maxLevel;
            levelDir = -1;
          } else if (effectiveLevel <= minLevel) {
            effectiveLevel = minLevel;
            levelDir = 1;
          }
        }
        level = effectiveLevel;
        
        // Chamber sweeps left and right, alternating direction each sweep
        const sweepGoingRight = (currentSweep % 2 === 0) ? (startPos.chamber <= 2) : (startPos.chamber > 2);
        if (sweepGoingRight) {
          chamber = minChamber + (progressInSweep * chamberRange);
        } else {
          chamber = maxChamber - (progressInSweep * chamberRange);
        }
        break;
      }
      
      case 'x-factor': {
        // X FACTOR: Diagonal movement - both chamber and level move together at same rate
        const chamberRange = maxChamber - minChamber;
        const levelRange = maxLevel - minLevel;
        const speed = Math.min(chamberRange, levelRange) / 20; // 20 seconds per diagonal
        
        // Determine initial diagonal direction based on start position
        let chamberDir, levelDir;
        if (startPos.chamber <= 2 && startPos.level <= 2) {
          chamberDir = 1; levelDir = 1; // Bottom-left: go up-right
        } else if (startPos.chamber > 2 && startPos.level <= 2) {
          chamberDir = -1; levelDir = 1; // Bottom-right: go up-left
        } else if (startPos.chamber <= 2 && startPos.level > 2) {
          chamberDir = 1; levelDir = -1; // Top-left: go down-right
        } else {
          chamberDir = -1; levelDir = -1; // Top-right: go down-left
        }
        
        chamber = oscillate(startPos.chamber, speed * chamberDir, elapsedSec, minChamber, maxChamber);
        level = oscillate(startPos.level, speed * levelDir, elapsedSec, minLevel, maxLevel);
        break;
      }
      
      case 'hour-glass': {
        // HOUR GLASS: Forms an hourglass shape
        // Path: (1,1) -> diagonal to (5,5) -> across to (1,5) -> diagonal to (5,1) -> across to (1,1)
        // 4 segments, each taking ~15 seconds = 60 seconds total cycle
        const segmentTime = 15; // seconds per segment
        const cycleTime = segmentTime * 4; // 60 seconds total
        const timeInCycle = elapsedSec % cycleTime;
        const currentSegment = Math.floor(timeInCycle / segmentTime);
        const progressInSegment = (timeInCycle % segmentTime) / segmentTime; // 0 to 1
        
        switch (currentSegment) {
          case 0:
            // Segment 1: (1,1) -> (5,5) diagonal up-right
            chamber = minChamber + (progressInSegment * (maxChamber - minChamber));
            level = minLevel + (progressInSegment * (maxLevel - minLevel));
            break;
          case 1:
            // Segment 2: (5,5) -> (1,5) across top (level stays at 5)
            chamber = maxChamber - (progressInSegment * (maxChamber - minChamber));
            level = maxLevel;
            break;
          case 2:
            // Segment 3: (1,5) -> (5,1) diagonal down-right
            chamber = minChamber + (progressInSegment * (maxChamber - minChamber));
            level = maxLevel - (progressInSegment * (maxLevel - minLevel));
            break;
          case 3:
            // Segment 4: (5,1) -> (1,1) across bottom (level stays at 1)
            chamber = maxChamber - (progressInSegment * (maxChamber - minChamber));
            level = minLevel;
            break;
          default:
            chamber = minChamber;
            level = minLevel;
        }
        break;
      }
      
      default:
        chamber = startPos.chamber;
        level = startPos.level;
    }
    
    return {
      chamber: Math.max(minChamber, Math.min(maxChamber, chamber)),
      level: Math.max(minLevel, Math.min(maxLevel, level))
    };
  }, []);

  // Get random journal prompt
  const getRandomPrompt = useCallback(() => {
    const categories = Object.keys(JOURNAL_PROMPTS);
    const randomCategory = categories[Math.floor(Math.random() * categories.length)];
    const prompts = JOURNAL_PROMPTS[randomCategory];
    const randomPrompt = prompts[Math.floor(Math.random() * prompts.length)];
    return { category: randomCategory, prompt: randomPrompt };
  }, []);

  // Define startPrep first so it can be used by other callbacks
  const startPrep = useCallback(() => {
    setIsRunning(true);
    setIsPaused(false);
    setCurrentPhase('prep');
    setPrepSeconds(0);
    
    // Calculate total cycles based on session time
    const cycles = Math.floor(TOTAL_SESSION_TIME / (EXERCISE_DURATION + BREAK_DURATION));
    setTotalCycles(cycles);
    
    // Get first exercise - need tool first to determine ride config constraints
    const { exercise, tool } = getRandomExercise(0, cycles);
    setCurrentExercise(exercise);
    setCurrentTool(tool);
    
    // Get initial ride configuration based on the tool and exercise
    // Rope: only levels 2-3, no X Factor
    // MotorBoat: only levels 1-2, no X Factor
    // Others: any position, X Factor starts from corners
    const initialRideConfig = getRandomRideConfig(tool, exercise);
    const initialStartPos = initialRideConfig.startPosition;
    
    // Set chamber and level from the start position (display as 1-5)
    setCurrentChamber(initialStartPos.chamber + 1);
    setCurrentLevel(initialStartPos.level + 1);
    
    // Store the start position and ride type for when workout begins
    setStartPosition(initialStartPos);
    setBarPosition(initialStartPos);
    setRideType(initialRideConfig.rideType);
    
    // Initialize configurations - tool/exercise-specific hand image + leg position text
    // MotorBoat: only horizontal-oriented images
    setCurrentHandConfig(getRandomHandConfig(tool, exercise));
    setCurrentLegPosition(getRandomLegPosition());
  }, [getRandomExercise, getRandomHandConfig, getRandomLegPosition, getRandomRideConfig]);

  // Start preparation phase
  // CAMERA TRACKING ARCHIVED
  const handleBeginSession = useCallback(() => {
    // Camera tracking is archived - skip directly to workout preparation
    setTrackingDecisionMade(true);
    startPrep();
  }, [startPrep]);

  // Handle user's tracking decision
  const handleTrackingDecision = useCallback((enableTracking) => {
    setShowTrackingPrompt(false);
    if (enableTracking) {
      setIsTrackingEnabled(true);
      // Check if user needs calibration
      if (!isCalibrated) {
        // Show calibration for first-time users
        setShowCalibration(true);
      } else {
        // User already calibrated, proceed to workout
        setTrackingDecisionMade(true);
        startPrep();
      }
    } else {
      // User skipped tracking, proceed to workout
      setTrackingDecisionMade(true);
      startPrep();
    }
  }, [isCalibrated, startPrep]);

  // Handle calibration completion
  const handleCalibrationComplete = useCallback((calibration) => {
    setCalibrationData(calibration);
    setIsCalibrated(true);
    setShowCalibration(false);
    setTrackingDecisionMade(true);
    startPrep();
  }, [startPrep]);

  // Handle calibration skip
  const handleCalibrationSkip = useCallback(() => {
    setShowCalibration(false);
    // Still enable tracking but without calibration
    setTrackingDecisionMade(true);
    startPrep();
  }, [startPrep]);

  // Handle recalibration request (from settings/camera bar)
  const handleRecalibrate = useCallback(() => {
    clearCalibration();
    setIsCalibrated(false);
    setCalibrationData(null);
    setShowCalibration(true);
  }, []);

  // Start workout after prep
  const startWorkout = useCallback(() => {
    setCurrentPhase('exercise');
    setTotalSeconds(0);
    setPhaseSeconds(0);
    setCycleCount(1);
    
    // Get next exercise
    const { exercise, tool } = getRandomExercise(1, totalCycles);
    setNextExercise(exercise);
    setNextTool(tool);
    
    // Grid starts at the position shown in prep (already set in startPrep)
    // Prepare the next ride config based on the next tool and exercise
    const nextRideConfig = getRandomRideConfig(tool, exercise);
    setNextRideType(nextRideConfig.rideType);
    setNextStartPosition(nextRideConfig.startPosition);
    
    setLinePosition({ col: 3, row: 2 });
    setLineAngle(0);
  }, [getRandomExercise, getRandomRideConfig, totalCycles]);

  // Reset workout
  const resetWorkout = useCallback(() => {
    setIsRunning(false);
    setIsPaused(false);
    setCurrentPhase('ready');
    setTotalSeconds(0);
    setPhaseSeconds(0);
    setPrepSeconds(0);
    setCycleCount(0);
    cycleCountRef.current = 0;
    setCurrentExercise('');
    setCurrentTool('');
    setNextExercise('');
    setNextTool('');
    setLinePosition({ col: 3, row: 2 });
    setLineAngle(0);
    setBarPosition({ chamber: 0, level: 0 });
    setRideType('level-ride');
    setJournalSeconds(0);
    setJournalEntry('');
    setCurrentPrompt({ category: '', prompt: '' });
    setCurrentChamber(1);
    setCurrentLevel(1);
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (breakIntervalRef.current) clearInterval(breakIntervalRef.current);
    if (journalIntervalRef.current) clearInterval(journalIntervalRef.current);
  }, []);

  // Start journal phase
  const startJournalPhase = useCallback(() => {
    setCurrentPhase('journal');
    setJournalSeconds(0);
    setCurrentPrompt(getRandomPrompt());
    setJournalEntry('');
  }, [getRandomPrompt]);

  // Get next journal prompt
  const nextPrompt = useCallback(() => {
    setCurrentPrompt(getRandomPrompt());
  }, [getRandomPrompt]);

  // Complete journal and finish session
  const finishSession = useCallback(() => {
    // Record workout completion for progress tracking
    recordWorkoutCompletion(totalSeconds, cycleCount);
    
    setCurrentPhase('complete');
    if (journalIntervalRef.current) clearInterval(journalIntervalRef.current);
    if (onComplete) onComplete();
  }, [onComplete, totalSeconds, cycleCount]);

  // Toggle pause
  const togglePause = useCallback(() => {
    setIsPaused(prev => !prev);
  }, []);

  // Prep timer effect
  useEffect(() => {
    if (currentPhase === 'prep' && !isPaused) {
      intervalRef.current = setInterval(() => {
        setPrepSeconds(prev => {
          const newSeconds = prev + 1;
          if (newSeconds >= PREP_DURATION) {
            startWorkout();
            return 0;
          }
          return newSeconds;
        });
      }, 1000);
    }
    
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [currentPhase, isPaused, startWorkout]);

  // Unified timer effect for exercise and break phases
  useEffect(() => {
    if (!isRunning || isPaused) return;
    if (currentPhase !== 'exercise' && currentPhase !== 'break') return;
    
    const phaseRef = { current: currentPhase };
    const phaseSecondsRef = { current: 0 };
    
    const tick = () => {
      // Update total seconds
      setTotalSeconds(prev => {
        const newTotal = prev + 1;
        if (newTotal >= TOTAL_SESSION_TIME) {
          setIsRunning(false);
          startJournalPhase();
          return prev;
        }
        return newTotal;
      });
      
      phaseSecondsRef.current += 1;
      setPhaseSeconds(phaseSecondsRef.current);
      
      if (phaseRef.current === 'exercise') {
        // Update line position for Rope
        if (phaseSecondsRef.current > 0 && phaseSecondsRef.current % POSITION_CHANGE_INTERVAL === 0) {
          if (currentTool === 'rope') {
            setLinePosition({
              col: Math.floor(Math.random() * GRID_COLS),
              row: Math.floor(Math.random() * GRID_ROWS)
            });
            setLineAngle(ANGLES[Math.floor(Math.random() * ANGLES.length)]);
          }
        }
        
        // Transition to break
        if (phaseSecondsRef.current >= EXERCISE_DURATION) {
          phaseRef.current = 'break';
          phaseSecondsRef.current = 0;
          setPhaseSeconds(0);
          setCurrentPhase('break');
          
          // Prepare next exercise
          const nextCycle = cycleCountRef.current + 1;
          const { exercise, tool } = getRandomExercise(nextCycle, totalCycles);
          setNextExercise(exercise);
          setNextTool(tool);
          
          const nextConfig = getRandomRideConfig(tool, exercise);
          setNextRideType(nextConfig.rideType);
          setNextStartPosition(nextConfig.startPosition);
          setCurrentChamber(nextConfig.startPosition.chamber + 1);
          setCurrentLevel(nextConfig.startPosition.level + 1);
          
          const isMotorboats = exercise === 'MotorBoat';
          const isSteering = exercise === 'Steer-R' || exercise === 'Steer-L';
          if (tool === 'long-bar') {
            // For Motorboats, ALWAYS use the dedicated motorboat config
            if (isMotorboats) {
              setCurrentHandConfig(MOTORBOAT_CONFIG);
            } else if (isSteering) {
              // For Steering, use the dedicated steer configs
              setCurrentHandConfig(STEER_LONGBAR_CONFIGS[Math.floor(Math.random() * STEER_LONGBAR_CONFIGS.length)]);
            } else {
              // For other Long Bar exercises, use regular configs
              setCurrentHandConfig(LONGBAR_CONFIGS[Math.floor(Math.random() * LONGBAR_CONFIGS.length)]);
            }
          } else {
            const configs = isMotorboats ? HAND_CONFIGS_HORIZONTAL : HAND_CONFIGS;
            setCurrentHandConfig(configs[Math.floor(Math.random() * configs.length)]);
          }
          // Pass the next exercise to leg position selector (excludes single leg for rowing)
          setCurrentLegPosition(getAssessmentAwareLegPosition(assessmentData, exercise));
        }
      } else if (phaseRef.current === 'break') {
        // Transition to exercise
        if (phaseSecondsRef.current >= BREAK_DURATION) {
          phaseRef.current = 'exercise';
          phaseSecondsRef.current = 0;
          setPhaseSeconds(0);
          setCurrentPhase('exercise');
          
          // Increment cycle count
          const newCycleCount = cycleCountRef.current + 1;
          setCycleCount(newCycleCount);
          cycleCountRef.current = newCycleCount;
          
          setCurrentExercise(nextExercise);
          setCurrentTool(nextTool);
          setRideType(nextRideType);
          setStartPosition(nextStartPosition);
          setBarPosition(nextStartPosition);
          setLinePosition({ col: 3, row: 2 });
          setLineAngle(0);
        }
      }
    };
    
    intervalRef.current = setInterval(tick, 1000);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRunning, isPaused, currentPhase, currentTool, totalCycles, nextExercise, nextTool, nextRideType, nextStartPosition, getRandomExercise, getRandomRideConfig, startJournalPhase]);

  // Bar position animation effect
  const animationFrameRef = useRef(null);
  const exerciseStartTimeRef = useRef(null);
  const currentRideTypeRef = useRef(rideType);
  const currentStartPositionRef = useRef(startPosition);
  const currentToolRef = useRef(currentTool);
  const currentExerciseRef = useRef(currentExercise);
  const lastPhaseRef = useRef(currentPhase);

  // Use useLayoutEffect to update refs before animation effect runs
  useLayoutEffect(() => {
    currentRideTypeRef.current = rideType;
    currentStartPositionRef.current = startPosition;
    currentToolRef.current = currentTool;
    currentExerciseRef.current = currentExercise;
  }, [rideType, startPosition, currentTool, currentExercise]);

  useEffect(() => {
    const useBarGrid = currentTool === 'empty-hand' || currentTool === 'mace' || currentTool === 'long-bar';
    
    // Reset exercise start time when entering exercise phase from any other phase
    if (currentPhase === 'exercise' && lastPhaseRef.current !== 'exercise') {
      exerciseStartTimeRef.current = null;
    }
    lastPhaseRef.current = currentPhase;
    
    if (currentPhase === 'exercise' && !isPaused && useBarGrid) {
      // Initialize barPosition to startPosition when exercise starts
      if (!exerciseStartTimeRef.current) {
        exerciseStartTimeRef.current = Date.now();
        // Ensure barPosition starts at exactly startPosition
        setBarPosition({ ...currentStartPositionRef.current });
      }

      const animate = () => {
        const elapsedMs = Date.now() - exerciseStartTimeRef.current;
        const newPosition = calculateBarPosition(
          Math.min(elapsedMs, EXERCISE_DURATION * 1000), 
          currentRideTypeRef.current, 
          currentStartPositionRef.current,
          currentToolRef.current,
          currentExerciseRef.current
        );
        
        setBarPosition(newPosition);
        animationFrameRef.current = requestAnimationFrame(animate);
      };

      animationFrameRef.current = requestAnimationFrame(animate);
    } else {
      if (currentPhase !== 'exercise') {
        exerciseStartTimeRef.current = null;
      }
    }

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [currentPhase, isPaused, currentTool, phaseSeconds, calculateBarPosition]);

  // Journal timer effect
  useEffect(() => {
    if (currentPhase === 'journal' && !isPaused) {
      journalIntervalRef.current = setInterval(() => {
        setJournalSeconds(prev => {
          const newSeconds = prev + 1;
          if (newSeconds >= JOURNAL_DURATION) {
            finishSession();
            return prev;
          }
          return newSeconds;
        });
      }, 1000);
    }

    return () => {
      if (journalIntervalRef.current) clearInterval(journalIntervalRef.current);
    };
  }, [currentPhase, isPaused, finishSession]);

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate remaining time in phase
  const getPhaseRemaining = () => {
    if (currentPhase === 'prep') return PREP_DURATION - prepSeconds;
    if (currentPhase === 'exercise') return EXERCISE_DURATION - phaseSeconds;
    if (currentPhase === 'break') return BREAK_DURATION - phaseSeconds;
    if (currentPhase === 'journal') return JOURNAL_DURATION - journalSeconds;
    return 0;
  };

  // Get tool display name
  const getToolName = (toolId) => {
    const tool = TOOLS.find(t => t.id === toolId);
    return tool ? tool.name : toolId;
  };

  // Render 6x5 grid cell (for Rope)
  const renderCell = (col, row) => {
    const isLineCell = linePosition.col === col && linePosition.row === row;
    
    return (
      <div
        key={`${col}-${row}`}
        className={`
          aspect-[3/4] border border-burnt-terra/20 rounded-sm
          flex items-center justify-center
          ${isLineCell ? 'bg-burnt-terra/20' : 'bg-white/50'}
          transition-all duration-500
        `}
      >
        {isLineCell && currentPhase === 'exercise' && (
          <div
            className="w-1.5 h-12 bg-burnt-terra rounded-full transition-transform duration-700 shadow-lg"
            style={{
              transform: `rotate(${lineAngle}deg)`,
              boxShadow: '0 0 8px rgba(196, 90, 59, 0.5)'
            }}
          />
        )}
      </div>
    );
  };

  // Render 5x5 grid with smooth sliding bar
  const render5x5Grid = () => {
    const cellSizePercent = 100 / CELLS_PER_ROW;
    const gapPercent = 2;
    const barLeftPercent = barPosition.chamber * (cellSizePercent + gapPercent * 0.3);
    const barTopPercent = (4 - barPosition.level) * (cellSizePercent + gapPercent * 0.3);
    
    return (
      <div className="w-full max-w-[300px] bg-ivory-mist/50 rounded-lg p-4 border-2 border-burnt-terra/20">
        <div className="flex mb-2">
          <div className="w-8"></div>
          <div className="flex-1 grid grid-cols-5 gap-2">
            {Array.from({ length: CHAMBER_COUNT }).map((_, i) => (
              <div key={i} className="text-center text-xs text-shadow-pine/50 font-body">
                {i + 1}
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex">
          <div className="w-8 flex flex-col">
            {Array.from({ length: LEVEL_COUNT }).map((_, i) => (
              <div key={i} className="flex-1 text-xs text-shadow-pine/50 font-body text-right pr-2 flex items-center justify-end">
                {LEVEL_COUNT - i}
              </div>
            ))}
          </div>
          
          <div className="flex-1 relative">
            <div className="grid grid-cols-5 gap-2">
              {Array.from({ length: LEVEL_COUNT * CHAMBER_COUNT }).map((_, idx) => (
                <div
                  key={idx}
                  className="aspect-square border-2 rounded-md bg-white/60 border-burnt-terra/20"
                />
              ))}
            </div>
            
            {currentPhase === 'exercise' && (
              <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                <div
                  className="absolute rounded-md bg-burnt-terra flex items-center justify-center shadow-lg"
                  style={{
                    width: `calc(${cellSizePercent}% - 4px)`,
                    height: `calc(${cellSizePercent}% - 4px)`,
                    left: `calc(${barLeftPercent}% + 2px)`,
                    top: `calc(${barTopPercent}% + 2px)`,
                    boxShadow: '0 0 20px rgba(196, 90, 59, 0.6), 0 0 40px rgba(196, 90, 59, 0.3)',
                    transition: 'none',
                  }}
                >
                  <div 
                    className="w-4 h-4 bg-ivory-mist rounded-full animate-pulse"
                    style={{ boxShadow: '0 0 10px rgba(245, 242, 235, 0.8)' }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render Figure 8 animation for Rope tool
  const renderFigure8 = () => {
    // Calculate position along figure 8 path based on elapsed time
    // Figure 8 parametric equations: x = sin(t), y = sin(t) * cos(t)
    const cycleTime = 8; // 8 seconds for one complete figure 8
    const t = (phaseSeconds / cycleTime) * Math.PI * 2;
    
    // Figure 8 (lemniscate) coordinates normalized to 0-1 range
    const scale = 0.35; // Scale of the figure 8
    const x = 0.5 + scale * Math.sin(t);
    const y = 0.5 + scale * Math.sin(t) * Math.cos(t);
    
    return (
      <div className="w-full max-w-[300px] bg-ivory-mist/50 rounded-lg p-4 border-2 border-burnt-terra/20">
        <div className="relative w-full aspect-square">
          {/* Figure 8 path outline */}
          <svg 
            className="absolute inset-0 w-full h-full" 
            viewBox="0 0 100 100"
            style={{ overflow: 'visible' }}
          >
            {/* Draw figure 8 path */}
            <path
              d="M 50 50 
                 C 50 20, 85 20, 85 50
                 C 85 80, 50 80, 50 50
                 C 50 20, 15 20, 15 50
                 C 15 80, 50 80, 50 50"
              fill="none"
              stroke="rgba(196, 90, 59, 0.2)"
              strokeWidth="3"
              strokeLinecap="round"
            />
            
            {/* Trail effect - fading circles behind cursor */}
            {currentPhase === 'exercise' && !isPaused && Array.from({ length: 5 }).map((_, i) => {
              const trailT = ((phaseSeconds - i * 0.15) / cycleTime) * Math.PI * 2;
              const trailX = 50 + 35 * Math.sin(trailT);
              const trailY = 50 + 35 * Math.sin(trailT) * Math.cos(trailT);
              return (
                <circle
                  key={i}
                  cx={trailX}
                  cy={trailY}
                  r={6 - i}
                  fill={`rgba(196, 90, 59, ${0.3 - i * 0.05})`}
                />
              );
            })}
          </svg>
          
          {/* Moving cursor */}
          {currentPhase === 'exercise' && (
            <div
              className="absolute w-6 h-6 rounded-full bg-burnt-terra shadow-lg transform -translate-x-1/2 -translate-y-1/2"
              style={{
                left: `${x * 100}%`,
                top: `${y * 100}%`,
                boxShadow: '0 0 20px rgba(196, 90, 59, 0.6), 0 0 40px rgba(196, 90, 59, 0.3)',
                transition: 'none',
              }}
            >
              <div 
                className="absolute inset-1 bg-ivory-mist rounded-full animate-pulse"
                style={{ boxShadow: '0 0 10px rgba(245, 242, 235, 0.8)' }}
              />
            </div>
          )}
          
          {/* Center label */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-burnt-terra/30 font-display text-lg font-bold">
              ROPE
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Get ride type display name
  const getRideTypeDisplay = (type) => {
    switch (type) {
      case 'level-ride': return 'Level Ride';
      case 'chamber-ride': return 'Chamber Ride';
      case 'chamber-lock': return 'Chamber Lock';
      case 'level-lock': return 'Level Lock';
      case 'x-factor': return 'X Factor';
      case 'hour-glass': return 'Hour Glass';
      default: return type;
    }
  };

  // Persistent Camera Tracking Bar - ARCHIVED: Camera functionality disabled
  const renderCameraTrackingBar = () => {
    // Camera tracking is archived - return null to hide the bar
    return null;
  };

  // ARCHIVED: Global Calibration Modal - Camera functionality disabled
  // if (showCalibration) { ... }

  // Ready screen with multi-tool selection
  if (currentPhase === 'ready') {
    return (
      <>
        {renderCameraTrackingBar()}
        <div className={`flex flex-col items-center justify-center p-6 space-y-6 min-h-[500px] ${isTrackingEnabled ? 'pt-16' : ''}`}>
          <h3 className="font-display text-2xl font-bold text-obsidian-night text-center">
            {sessionTitle || 'Calibration Session'}
          </h3>
          
          {/* Multi-Tool Selection */}
          <div className="w-full max-w-md space-y-3">
            <p className="text-sm text-shadow-pine/70 font-body text-center">Select your tools (one or more):</p>
          <div className="grid grid-cols-2 gap-3">
            {TOOLS.map((tool) => {
              const IconComponent = tool.icon;
              const isSelected = selectedTools.includes(tool.id);
              return (
                <button
                  key={tool.id}
                  data-testid={`tool-${tool.id}`}
                  onClick={() => toggleTool(tool.id)}
                  className={`
                    p-4 rounded-lg border-2 transition-all text-left relative
                    ${isSelected 
                      ? 'border-burnt-terra bg-burnt-terra/10 shadow-md' 
                      : 'border-burnt-terra/20 bg-white hover:border-burnt-terra/50 hover:bg-burnt-terra/5'
                    }
                  `}
                >
                  {isSelected && (
                    <div className="absolute top-2 right-2">
                      <Check className="w-4 h-4 text-burnt-terra" />
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-1">
                    <IconComponent className={`w-5 h-5 ${isSelected ? 'text-burnt-terra' : 'text-shadow-pine/60'}`} />
                    <span className={`font-semibold font-body ${isSelected ? 'text-burnt-terra' : 'text-obsidian-night'}`}>
                      {tool.name}
                    </span>
                  </div>
                  <p className="text-xs text-shadow-pine/60 font-body">{tool.description}</p>
                </button>
              );
            })}
          </div>
          {selectedTools.includes('rope') && (
            <p className="text-xs text-burnt-terra/70 font-body text-center italic">
              Rope exercises will appear in the last 2 exercises of the session
            </p>
          )}
        </div>
        
        <p className="text-shadow-pine/80 font-body text-center text-sm max-w-md">
          30-second prep → 15-minute session → 5-minute reflection
        </p>
        
        <div className="grid grid-cols-3 gap-4 text-center text-sm">
          <div className="p-3 bg-shadow-pine/10 rounded-lg">
            <div className="font-bold text-shadow-pine">30 sec</div>
            <div className="text-shadow-pine/60 font-body">Prepare</div>
          </div>
          <div className="p-3 bg-burnt-terra/10 rounded-lg">
            <div className="font-bold text-burnt-terra">15 min</div>
            <div className="text-shadow-pine/60 font-body">Workout</div>
          </div>
          <div className="p-3 bg-shadow-pine/10 rounded-lg">
            <div className="font-bold text-shadow-pine">5 min</div>
            <div className="text-shadow-pine/60 font-body">Journal</div>
          </div>
        </div>
        <Button
          onClick={handleBeginSession}
          size="lg"
          className="bg-burnt-terra text-ivory-mist hover:bg-[#A54A2F] font-semibold px-12"
          data-testid="begin-session-button"
        >
          <Play className="w-5 h-5 mr-2" />
          Begin Session
        </Button>
        
        {/* ARCHIVED: Movement Tracking Prompt Modal - Camera functionality disabled */}
        {/* showTrackingPrompt modal removed */}

        {/* ARCHIVED: Camera Calibration Modal - Camera functionality disabled */}
        {/* showCalibration modal removed */}
        </div>
      </>
    );
  }

  // Prep screen - 30 seconds to prepare with both hand and leg configs - zero scroll
  if (currentPhase === 'prep') {
    // Get GIF for current exercise (prep shows the CURRENT exercise, not next)
    const currentExerciseGif = getMovementGif(currentExercise);
    const shouldShowGifInPrep = showGifInBreak && currentExerciseGif;
    
    return (
      <>
        {renderCameraTrackingBar()}
        <div className={`flex flex-col items-center justify-between p-2 h-[calc(100vh-120px)] max-h-[700px] bg-gradient-to-br from-shadow-pine/5 to-burnt-terra/5 rounded-lg ${isTrackingEnabled ? 'mt-12' : ''}`}>
          {/* Top section - timer and info */}
          <div className="flex flex-col items-center gap-1 w-full">
            <div className="text-shadow-pine font-body font-semibold uppercase tracking-wider text-sm">
              Prepare
            </div>
          
            <div className="text-4xl font-display font-bold text-obsidian-night">
              {PREP_DURATION - prepSeconds}
            </div>

            {/* Starting position and movement info - compact inline */}
            <div className="flex items-center gap-2 text-center flex-wrap justify-center">
              {currentTool !== 'rope' && (
                <>
                  <div className="px-2 py-1 bg-burnt-terra/10 rounded">
                    <span className="text-xs text-shadow-pine/60 font-body">Ch:</span>
                    <span className="font-bold text-burnt-terra ml-1">{currentChamber}</span>
                  </div>
                  <div className="px-2 py-1 bg-shadow-pine/10 rounded">
                    <span className="text-xs text-shadow-pine/60 font-body">Lv:</span>
                    <span className="font-bold text-shadow-pine ml-1">{currentLevel}</span>
                  </div>
                </>
              )}
              <div className="px-2 py-1 bg-burnt-terra/10 rounded">
                <span className="text-xs text-shadow-pine/60 font-body">Tool:</span>
                <span className="font-bold text-burnt-terra text-sm ml-1">{getToolName(currentTool)}</span>
              </div>
              <div className="px-2 py-1 bg-shadow-pine/10 rounded">
                <span className="text-xs text-shadow-pine/60 font-body">Move:</span>
                <span className="font-bold text-shadow-pine text-sm ml-1">{currentExercise}</span>
              </div>
              {currentTool !== 'rope' && (
                <div className="px-2 py-1 bg-burnt-terra/10 rounded">
                  <span className="text-xs text-shadow-pine/60 font-body">Pattern:</span>
                  <span className="font-bold text-burnt-terra text-sm ml-1">{getRideTypeDisplay(rideType)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Middle section - GIF/Configuration Image (oscillates) */}
          <div className="flex-1 flex flex-col items-center justify-center w-full min-h-0 py-2">
            {shouldShowGifInPrep ? (
              /* Show Movement GIF */
              <div className="flex flex-col items-center w-full h-full">
                <div className="text-xs text-burnt-terra font-semibold uppercase tracking-wider text-center font-body mb-1">
                  {currentExercise} Movement
                </div>
                <div className="flex items-center justify-center flex-1 w-full min-h-0">
                  <img 
                    src={currentExerciseGif} 
                    alt={`${currentExercise} movement`}
                    className="max-w-full max-h-[40vh] object-contain rounded-lg"
                    style={{ imageRendering: 'auto' }}
                  />
                </div>
              </div>
            ) : currentTool === 'rope' ? (
              <div className="flex flex-col items-center w-full h-full">
                <div className="text-xs text-burnt-terra font-semibold uppercase tracking-wider text-center font-body mb-1">
                  Rope Configuration
                </div>
                <div className="flex items-center justify-center flex-1 w-full min-h-0">
                  <img 
                    src={ROPE_CONFIG.image} 
                    alt={ROPE_CONFIG.name}
                    className="max-w-full max-h-[40vh] object-contain"
                  />
                </div>
              </div>
            ) : currentHandConfig && (
              <div className="flex flex-col items-center w-full h-full">
                <div className="text-xs text-burnt-terra font-semibold uppercase tracking-wider text-center font-body mb-1">
                  {currentTool === 'long-bar' ? 'Long Bar Configuration' : 'Hand Configuration'}
                </div>
                <div className="flex items-center justify-center flex-1 w-full min-h-0">
                  <img 
                    src={currentHandConfig.image} 
                    alt={currentHandConfig.name}
                    className="max-w-full max-h-[40vh] object-contain"
                  />
                </div>
              </div>
            )}

            {/* Leg Position - compact */}
            {currentLegPosition && (
              <div className="text-center mt-1">
                <span className="text-xs text-shadow-pine/60 font-body uppercase tracking-wider mr-2">Leg:</span>
                <span className="text-lg font-display font-bold text-shadow-pine">{currentLegPosition}</span>
                {currentLegPosition.startsWith('Single Leg') && (
                  <p className="text-xs text-burnt-terra font-body italic mt-1">
                    Use both legs if you need to regain balance
                  </p>
                )}
              </div>
            )}
            
            {/* Oscillation indicator */}
            {currentExerciseGif && (
              <div className="flex gap-1 mt-1">
                <div className={`w-2 h-2 rounded-full transition-all ${showGifInBreak ? 'bg-burnt-terra' : 'bg-shadow-pine/30'}`} />
                <div className={`w-2 h-2 rounded-full transition-all ${!showGifInBreak ? 'bg-burnt-terra' : 'bg-shadow-pine/30'}`} />
              </div>
            )}
          </div>

          {/* Bottom - Pause button */}
          <Button
            onClick={togglePause}
            variant="outline"
            size="sm"
            className="border-burnt-terra/30 text-shadow-pine hover:bg-burnt-terra/10"
          >
            {isPaused ? <Play className="w-4 h-4 mr-2" /> : <Pause className="w-4 h-4 mr-2" />}
            {isPaused ? 'Resume' : 'Pause'}
          </Button>
        </div>
      </>
    );
  }

  // Journal screen
  if (currentPhase === 'journal') {
    const remainingTime = JOURNAL_DURATION - journalSeconds;
    const minutes = Math.floor(remainingTime / 60);
    const seconds = remainingTime % 60;
    
    return (
      <>
        {renderCameraTrackingBar()}
        <div className={`flex flex-col p-6 space-y-6 min-h-[500px] bg-gradient-to-br from-shadow-pine/5 to-burnt-terra/5 rounded-lg ${isTrackingEnabled ? 'mt-12' : ''}`}>
          <div className="text-center space-y-2">
            <div className="flex items-center justify-center gap-2 text-burnt-terra">
              <BookOpen className="w-6 h-6" />
              <span className="font-display text-xl font-bold">Post-Workout Reflection</span>
            </div>
          </div>

          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-full border border-burnt-terra/20 shadow-sm">
              <span className="text-shadow-pine/60 font-body text-sm">Time remaining:</span>
              <span className="font-display text-2xl font-bold text-obsidian-night">
                {minutes}:{seconds.toString().padStart(2, '0')}
              </span>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 border border-burnt-terra/20 shadow-md space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-burnt-terra font-semibold uppercase tracking-wider font-body">
              {currentPrompt.category}
            </span>
            <Button
              onClick={nextPrompt}
              variant="ghost"
              size="sm"
              className="text-shadow-pine hover:text-burnt-terra text-xs"
            >
              <ChevronRight className="w-4 h-4 mr-1" />
              New Prompt
            </Button>
          </div>
          <p className="font-display text-lg text-obsidian-night leading-relaxed">
            {currentPrompt.prompt}
          </p>
        </div>

        <div className="flex-1 space-y-2">
          <Textarea
            placeholder="Write your thoughts here..."
            value={journalEntry}
            onChange={(e) => setJournalEntry(e.target.value)}
            className="min-h-[150px] bg-white border-burnt-terra/30 text-obsidian-night focus:border-burnt-terra resize-none"
          />
        </div>

        <div className="flex gap-3 justify-center pt-2">
          <Button
            onClick={togglePause}
            variant="outline"
            size="sm"
            className="border-burnt-terra/30 text-shadow-pine hover:bg-burnt-terra/10"
          >
            {isPaused ? <Play className="w-4 h-4 mr-2" /> : <Pause className="w-4 h-4 mr-2" />}
            {isPaused ? 'Resume' : 'Pause'}
          </Button>
          <Button
            onClick={finishSession}
            className="bg-burnt-terra text-ivory-mist hover:bg-[#A54A2F] font-semibold"
          >
            Complete Session
          </Button>
        </div>
        
        {/* Movement Summary Button - Show if any tracking data was collected */}
        {movementHistory.length > 0 && (
          <div className="flex justify-center pt-2">
            <Button
              onClick={showSummary}
              variant="outline"
              className="border-shadow-pine/30 text-shadow-pine hover:bg-shadow-pine/10"
            >
              <Camera className="w-4 h-4 mr-2" />
              View Movement Analysis ({movementHistory.length} data points)
            </Button>
          </div>
        )}
        
        {/* Movement Summary Modal */}
        {showMovementSummary && (
          <MovementSummary
            movementHistory={movementHistory}
            rideType={rideType}
            expectedChamber={Math.round(barPosition.chamber) + 1}
            expectedLevel={Math.round(barPosition.level) + 1}
            exerciseName="Full Workout Session"
            assessmentData={assessmentData}
            onClose={() => setShowMovementSummary(false)}
            onRetry={() => {
              setShowMovementSummary(false);
              setMovementHistory([]);
              exerciseMovementHistoryRef.current = [];
            }}
          />
        )}
        </div>
      </>
    );
  }

  // Complete screen
  if (currentPhase === 'complete') {
    return (
      <>
        {renderCameraTrackingBar()}
        <div className={`flex flex-col items-center justify-center p-8 space-y-6 min-h-[400px] ${isTrackingEnabled ? 'mt-12' : ''}`}>
          <div className="w-20 h-20 bg-shadow-pine/20 rounded-full flex items-center justify-center">
            <span className="text-4xl">✓</span>
          </div>
          <h3 className="font-display text-2xl font-bold text-obsidian-night">
            Session Complete!
          </h3>
          <p className="text-shadow-pine/80 font-body text-center">
            You completed {cycleCount} exercise cycles. Well done!
          </p>
          {movementHistory.length > 0 && (
            <Button
              onClick={showSummary}
              className="bg-burnt-terra text-white hover:bg-burnt-terra/90"
            >
              <Camera className="w-4 h-4 mr-2" />
              View Movement Analysis
            </Button>
          )}
          <Button
            onClick={resetWorkout}
            variant="outline"
            className="border-burnt-terra/30 text-shadow-pine hover:bg-burnt-terra/10"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Start Again
          </Button>
          
          {/* Movement Summary Modal */}
          {showMovementSummary && (
            <MovementSummary
              movementHistory={movementHistory}
              rideType={rideType}
              expectedChamber={Math.round(barPosition.chamber) + 1}
              expectedLevel={Math.round(barPosition.level) + 1}
              exerciseName="Full Workout Session"
              assessmentData={assessmentData}
              onClose={() => setShowMovementSummary(false)}
              onRetry={() => {
                setShowMovementSummary(false);
                setMovementHistory([]);
                exerciseMovementHistoryRef.current = [];
              }}
            />
          )}
        </div>
      </>
    );
  }

  // Break screen - Special box breathing UI for last break
  if (currentPhase === 'break') {
    // Last break - show box breathing
    if (isLastBreak) {
      const ballScale = getBreathBallScale();
      const instruction = getBoxBreathInstruction();
      
      return (
        <>
          {renderCameraTrackingBar()}
          <div className={`flex flex-col items-center justify-between p-2 h-[calc(100vh-120px)] max-h-[700px] bg-gradient-to-br from-shadow-pine/20 to-burnt-terra/20 rounded-lg ${isTrackingEnabled ? 'mt-12' : ''}`}>
            {/* Top section */}
            <div className="flex flex-col items-center gap-1">
              <div className="flex items-center gap-3 text-xs">
                <span className="text-shadow-pine/60 font-body">Total: {formatTime(totalSeconds)}</span>
                <span className="text-shadow-pine/60 font-body">|</span>
                <span className="text-shadow-pine/60 font-body">Final Cooldown</span>
              </div>

              <div className="text-burnt-terra font-body font-semibold uppercase tracking-wider text-sm">
                Box Breathing
              </div>
              
              <div className="text-3xl font-display font-bold text-obsidian-night">
                {getPhaseRemaining()}
              </div>
            </div>
            
            {/* Middle section - Breathing animation */}
            <div className="flex-1 flex flex-col items-center justify-center min-h-0">
              {/* Breathing instruction */}
              <div className="text-shadow-pine font-body font-medium text-base text-center max-w-md px-4 mb-2">
                {instruction}
              </div>
              
              {/* Pulsing ball animation */}
              <div className="flex items-center justify-center h-36 w-36">
                <div 
                  className="rounded-full bg-gradient-to-br from-burnt-terra to-shadow-pine transition-all duration-1000 ease-in-out"
                  style={{
                    width: `${ballScale * 80}px`,
                    height: `${ballScale * 80}px`,
                    boxShadow: `0 0 ${ballScale * 25}px rgba(194, 93, 63, 0.5)`
                  }}
                />
              </div>
              
              {/* Phase indicator */}
              {boxBreathPhase !== 'prep' && (
                <div className="flex gap-2 items-center mt-2">
                  {['exhale', 'hold1', 'inhale', 'hold2'].map((phase, idx) => (
                    <div 
                      key={phase}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${
                        boxBreathPhase === phase 
                          ? 'bg-burnt-terra scale-125' 
                          : 'bg-shadow-pine/30'
                      }`}
                    />
                  ))}
                  <span className="text-shadow-pine/60 font-body text-xs ml-2">
                    Cycle {boxBreathCycle + 1}/2
                  </span>
                </div>
              )}
            </div>

            {/* Bottom - Pause button */}
            <Button
              onClick={togglePause}
              variant="outline"
              size="sm"
              className="border-burnt-terra/30 text-shadow-pine hover:bg-burnt-terra/10"
            >
              {isPaused ? <Play className="w-4 h-4 mr-2" /> : <Pause className="w-4 h-4 mr-2" />}
              {isPaused ? 'Resume' : 'Pause'}
            </Button>
          </div>
        </>
      );
    }
    
    // Regular break screen - zero scroll design with GIF/hand config oscillation
    const nextExerciseGif = getMovementGif(nextExercise);
    const shouldShowGif = showGifInBreak && nextExerciseGif;
    
    return (
      <>
        {renderCameraTrackingBar()}
        <div className={`flex flex-col items-center justify-between p-2 h-[calc(100vh-120px)] max-h-[700px] bg-gradient-to-br from-shadow-pine/10 to-burnt-terra/10 rounded-lg ${isTrackingEnabled ? 'mt-12' : ''}`}>
          {/* Top section - timer and info */}
          <div className="flex flex-col items-center gap-1 w-full">
            <div className="flex items-center gap-3 text-xs">
              <span className="text-shadow-pine/60 font-body">Total: {formatTime(totalSeconds)}</span>
              <span className="text-shadow-pine/60 font-body">|</span>
              <span className="text-shadow-pine/60 font-body">Cycle {cycleCount}/{totalCycles}</span>
            </div>

            <div className="text-burnt-terra font-body font-semibold uppercase tracking-wider text-sm">
              Rest Break
            </div>
            
            <div className="text-3xl font-display font-bold text-obsidian-night">
              {getPhaseRemaining()}
            </div>
            
            <div className="text-burnt-terra/80 font-body font-medium text-xs italic">
              Keep Clavicle Lifted
            </div>
          </div>

          {/* Middle section - Image/GIF display (oscillates) */}
          <div className="flex-1 flex flex-col items-center justify-center w-full min-h-0 py-2">
            {shouldShowGif ? (
              /* Show Movement GIF */
              <div className="flex flex-col items-center w-full h-full">
                <div className="text-xs text-burnt-terra font-semibold uppercase tracking-wider text-center font-body mb-1">
                  {nextExercise} Movement
                </div>
                <div className="flex items-center justify-center flex-1 w-full min-h-0">
                  <img 
                    src={nextExerciseGif} 
                    alt={`${nextExercise} movement`}
                    className="max-w-full max-h-[35vh] object-contain rounded-lg"
                    style={{ imageRendering: 'auto' }}
                  />
                </div>
              </div>
            ) : nextTool === 'rope' ? (
              /* Show Rope Configuration */
              <div className="flex flex-col items-center w-full h-full">
                <div className="text-xs text-burnt-terra font-semibold uppercase tracking-wider text-center font-body mb-1">
                  Rope Configuration
                </div>
                <div className="flex items-center justify-center flex-1 w-full min-h-0">
                  <img 
                    src={ROPE_CONFIG.image} 
                    alt={ROPE_CONFIG.name}
                    className="max-w-full max-h-[35vh] object-contain"
                  />
                </div>
              </div>
            ) : currentHandConfig && (
              /* Show Hand Configuration */
              <div className="flex flex-col items-center w-full h-full">
                <div className="text-xs text-burnt-terra font-semibold uppercase tracking-wider text-center font-body mb-1">
                  {nextTool === 'long-bar' ? 'Long Bar Configuration' : 'Hand Configuration'}
                </div>
                <div className="flex items-center justify-center flex-1 w-full min-h-0">
                  <img 
                    src={currentHandConfig.image} 
                    alt={currentHandConfig.name}
                    className="max-w-full max-h-[35vh] object-contain"
                  />
                </div>
              </div>
            )}

            {/* Leg Position - compact */}
            {currentLegPosition && (
              <div className="text-center mt-1">
                <span className="text-xs text-shadow-pine/60 font-body uppercase tracking-wider mr-2">Leg:</span>
                <span className="text-sm font-display font-bold text-shadow-pine">{currentLegPosition}</span>
              </div>
            )}
            
            {/* Oscillation indicator */}
            {nextExerciseGif && (
              <div className="flex gap-1 mt-1">
                <div className={`w-2 h-2 rounded-full transition-all ${showGifInBreak ? 'bg-burnt-terra' : 'bg-shadow-pine/30'}`} />
                <div className={`w-2 h-2 rounded-full transition-all ${!showGifInBreak ? 'bg-burnt-terra' : 'bg-shadow-pine/30'}`} />
              </div>
            )}
          </div>

          {/* Bottom section - Next exercise info */}
          <div className="flex flex-col items-center gap-2 w-full">
            <div className="flex items-center gap-2 text-center flex-wrap justify-center">
              <div className="px-2 py-1 bg-shadow-pine/10 rounded">
                <span className="text-xs text-shadow-pine/60 font-body">Ch:</span>
                <span className="font-bold text-shadow-pine text-sm ml-1">{currentChamber}</span>
              </div>
              <div className="px-2 py-1 bg-shadow-pine/10 rounded">
                <span className="text-xs text-shadow-pine/60 font-body">Lv:</span>
                <span className="font-bold text-shadow-pine text-sm ml-1">{currentLevel}</span>
              </div>
              <div className="px-2 py-1 bg-burnt-terra/10 rounded">
                <span className="text-xs text-shadow-pine/60 font-body">Tool:</span>
                <span className="font-bold text-burnt-terra text-sm ml-1">{getToolName(nextTool)}</span>
              </div>
              <div className="px-2 py-1 bg-shadow-pine/10 rounded">
                <span className="text-xs text-shadow-pine/60 font-body">Pattern:</span>
                <span className="font-bold text-shadow-pine text-sm ml-1">{getRideTypeDisplay(nextRideType)}</span>
              </div>
            </div>
            
            <div className="text-center">
              <p className="text-shadow-pine/60 font-body text-xs">Next:</p>
              <h3 className="font-display text-lg font-bold text-burnt-terra">
                {nextExercise}
              </h3>
            </div>

            <Button
              onClick={togglePause}
              variant="outline"
              size="sm"
              className="border-burnt-terra/30 text-shadow-pine hover:bg-burnt-terra/10"
            >
              {isPaused ? <Play className="w-4 h-4 mr-2" /> : <Pause className="w-4 h-4 mr-2" />}
              {isPaused ? 'Resume' : 'Pause'}
            </Button>
          </div>
        </div>
      </>
    );
  }

  // Exercise screen
  const useRopeGrid = currentTool === 'rope';
  
  // BodyGrid View archived for future refinement
  // if (viewMode === 'body-grid' && currentTool !== 'rope') {
  //   return (
  //     <div className="flex flex-col h-[85vh] sm:h-[80vh]">
  //       <BodyGrid
  //         currentLevel={Math.round(barPosition.level) + 1}
  //         currentChamber={Math.round(barPosition.chamber) + 1}
  //         currentExercise={currentExercise}
  //         currentTool={getToolName(currentTool)}
  //         rideType={getRideTypeDisplay(rideType)}
  //         isActive={currentPhase === 'exercise' && !isPaused}
  //         onClose={() => setViewMode('standard')}
  //       />
  //       
  //       {/* Floating controls */}
  //       <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-3 bg-white/95 backdrop-blur px-4 py-2 rounded-full shadow-lg border border-burnt-terra/20">
  //         <span className="text-shadow-pine font-body text-sm font-medium">
  //           {getPhaseRemaining()}s
  //         </span>
  //         <div className="w-px h-6 bg-shadow-pine/20" />
  //         <Button
  //           onClick={togglePause}
  //           variant="ghost"
  //           size="sm"
  //           className="text-shadow-pine hover:bg-burnt-terra/10 p-2"
  //         >
  //           {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
  //         </Button>
  //         <Button
  //           onClick={resetWorkout}
  //           variant="ghost"
  //           size="sm"
  //           className="text-shadow-pine hover:bg-burnt-terra/10 p-2"
  //         >
  //           <RotateCcw className="w-5 h-5" />
  //         </Button>
  //       </div>
  //     </div>
  //   );
  // }
  
  return (
    <>
      {renderCameraTrackingBar()}
      <div className={`flex flex-col h-[calc(100vh-120px)] max-h-[700px] ${isTrackingEnabled ? 'mt-12' : ''}`}>
        {/* Header - compact */}
        <div className="flex items-center justify-between px-3 py-1">
          <div className="text-xs text-shadow-pine/60 font-body">
            {formatTime(totalSeconds)} / {formatTime(TOTAL_SESSION_TIME)}
          </div>
          <div className="px-2 py-0.5 bg-burnt-terra/10 rounded text-xs font-semibold text-burnt-terra font-body">
            {getToolName(currentTool)}
          </div>
          <div className="text-xs text-shadow-pine/60 font-body">
            Cycle {cycleCount}/{totalCycles}
          </div>
        </div>

        {/* Current exercise - compact */}
        <div className="text-center py-2 bg-burnt-terra/10 rounded-lg mx-3">
          <p className="text-xs text-shadow-pine/60 font-body uppercase tracking-wider">Current Movement</p>
          <h3 className="font-display text-lg font-bold text-burnt-terra">
            {currentExercise}
          </h3>
        </div>

        {/* Chamber, Level, Ride type - inline for Rope hidden */}
        {currentTool !== 'rope' && (
          <div className="flex justify-center items-center gap-3 py-1">
            <div className="flex items-center gap-1">
              <span className="text-xs text-shadow-pine/60 font-body">Ch:</span>
              <span className="font-bold text-shadow-pine">{Math.round(barPosition.chamber) + 1}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-shadow-pine/60 font-body">Lv:</span>
              <span className="font-bold text-burnt-terra">{Math.round(barPosition.level) + 1}</span>
            </div>
            <span className="px-2 py-0.5 bg-shadow-pine/10 rounded-full text-xs font-semibold text-shadow-pine font-body">
              {getRideTypeDisplay(rideType)}
            </span>
          </div>
        )}

        {/* Grid or Figure 8 - flexible height */}
        <div className="flex-1 flex justify-center items-center px-3 min-h-0">
          {currentTool === 'rope' ? (
            renderFigure8()
          ) : (
            render5x5Grid()
          )}
        </div>

        {/* Timer and Progress - compact */}
        <div className="px-3 py-1">
          <div className="flex items-center justify-center gap-3 mb-1">
            <span className="text-shadow-pine/60 font-body text-xs">Time:</span>
            <span className="font-display text-lg font-bold text-obsidian-night">
              {getPhaseRemaining()}s
            </span>
          </div>
          <div className="h-1.5 bg-burnt-terra/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-burnt-terra transition-all duration-1000"
              style={{ width: `${(phaseSeconds / EXERCISE_DURATION) * 100}%` }}
            />
          </div>
        </div>

        {/* Controls - compact */}
        <div className="flex justify-center gap-2 py-2">
          <Button
            onClick={togglePause}
            variant="outline"
            size="sm"
            className="border-burnt-terra/30 text-shadow-pine hover:bg-burnt-terra/10 h-8 px-3"
          >
            {isPaused ? <Play className="w-4 h-4 mr-1" /> : <Pause className="w-4 h-4 mr-1" />}
            {isPaused ? 'Resume' : 'Pause'}
          </Button>
          <Button
            onClick={resetWorkout}
            variant="outline"
            size="sm"
            className="border-shadow-pine/30 text-shadow-pine hover:bg-shadow-pine/10 h-8 px-3"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Reset
          </Button>
        </div>
      </div>
      
      {/* Movement Summary Modal - Shows full session analysis */}
      {showMovementSummary && (
        <MovementSummary
          movementHistory={movementHistory}
          rideType={rideType}
          expectedChamber={Math.round(barPosition.chamber) + 1}
          expectedLevel={Math.round(barPosition.level) + 1}
          exerciseName="Full Workout Session"
          assessmentData={assessmentData}
          onClose={() => setShowMovementSummary(false)}
          onRetry={() => {
            setShowMovementSummary(false);
            setMovementHistory([]);
            exerciseMovementHistoryRef.current = [];
          }}
        />
      )}
    </>
  );
};

export default WorkoutGrid;
